<script>
import { getSiteInspections, getSiteInspectionStats, deleteSiteInspection } from '@/utils/site-inspection'
import { getSiteInspectorDropdown } from '@/utils/site-inspector'
import AssignInspectorModal from '@/components/modal/assign-inspector-modal.vue'
import UploadInspectionDocModal from '@/components/modal/upload-inspection-doc-modal.vue'
import SiteInspectionDetailModal from '@/components/modal/site-inspection-detail-modal.vue'

export default {

    components: {
        AssignInspectorModal,
        UploadInspectionDocModal,
        SiteInspectionDetailModal
    },

    inject: ['setLoading'],

    data() {
        return {
            title: "CRM",
            text: "CRM",
            loading: true,
            data: [],
            stats: [],
            pagination: null,
            columns: [
                { key: 'inspection_number', label: 'Inspection #' },
                { key: 'party', label: 'Party' },
                { key: 'site_inspector', label: 'Site Inspector' },
                { key: 'status', label: 'Status' },
                { key: 'created_by', label: 'Created By' },
                { key: 'created_at', label: 'Created' },
                { key: 'action', label: 'Action', width: '60px' }
            ]
        }
    },

    computed: {
        text1() {
            return `Site Inspections (${this.$numFormat(this.pagination?.total || 0)})`
        },
        statCards() {
            const map = {
                pending: { label: 'Pending', icon: 'ti ti-clock', color: '#f59e0b', bg: '#fffbeb' },
                documents_uploaded: { label: 'Docs Uploaded', icon: 'ti ti-file-check', color: '#3b82f6', bg: '#eff6ff' },
                completed: { label: 'Completed', icon: 'ti ti-circle-check', color: '#22c55e', bg: '#f0fdf4' }
            }
            return this.stats.map(s => ({
                ...map[s.status],
                count: s.count,
                status: s.status
            }))
        }
    },

    mounted() {
        this.getData()
        this.getStats()
    },

    methods: {

        async getData(page = 1) {
            this.loading = true
            try {
                const res = await getSiteInspections(page)
                const resData = res?.data?.data
                this.data = resData?.data || []
                this.pagination = {
                    current_page: resData.current_page,
                    per_page: resData.per_page,
                    last_page: resData.last_page,
                    total: resData.total,
                    links: resData.links
                }
            } catch (error) {
                this.$swalToast(error.message, 'error')
            } finally {
                this.loading = false
            }
        },

        async getStats() {
            try {
                const res = await getSiteInspectionStats()
                this.stats = res?.data?.data || []
            } catch (error) {
                console.log(error.message)
            }
        },

        statusBadge(status) {
            const map = {
                pending: { label: 'Pending', class: 'bg-warning-light text-warning' },
                documents_uploaded: { label: 'Docs Uploaded', class: 'bg-info-light text-info' },
                completed: { label: 'Completed', class: 'bg-success-light text-success' }
            }
            return map[status] || { label: status, class: 'bg-light text-dark' }
        },

        confirmDelete(id) {
            this.$swal({
                title: "Confirm Delete",
                text: "You want to delete this site inspection!",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes Delete",
            }).then((result) => {
                if (result.isConfirmed) {
                    this.remove(id)
                }
            })
        },

        async remove(id) {
            this.setLoading(true)
            try {
                const res = await deleteSiteInspection(id)
                this.$swalToast(res.data.message)
                this.getData()
                this.getStats()
            } catch (error) {
                this.$swalToast(error.message, 'error')
            } finally {
                this.setLoading(false)
            }
        },

        onAssigned() {
            this.getData()
            this.getStats()
        },

        onDocUploaded() {
            this.getData()
            this.getStats()
        }
    }

}
</script>

<template>

    <layout-header />
    <layout-sidebar />

    <div class="page-wrapper">
        <div class="content">

            <!-- Breadcrumb -->
            <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                <breadcrumb :title="title" :text="text" :text1="text1" />
            </div>

            <!-- Stats Cards -->
            <div class="row mb-3" v-if="statCards.length">
                <div class="col-xl-4 col-md-4" v-for="card in statCards" :key="card.status">
                    <div class="card border-0 shadow-sm" style="border-radius:12px;">
                        <div class="card-body d-flex align-items-center gap-3 py-3">
                            <div class="d-flex align-items-center justify-content-center rounded-circle"
                                :style="{ background: card.bg, width: '42px', height: '42px' }">
                                <i :class="card.icon" :style="{ color: card.color, fontSize: '20px' }"></i>
                            </div>
                            <div>
                                <div style="font-size:20px; font-weight:700; color:#0f172a;">{{ card.count }}</div>
                                <div style="font-size:11.5px; color:#94a3b8; font-weight:500;">{{ card.label }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Main Card -->
            <div class="card border-0 shadow-sm mb-0" style="border-radius:14px;">

                <div class="card-header bg-white border-bottom d-flex align-items-center justify-content-between py-3 px-4">
                    <h5 class="mb-0 fw-bold" style="font-size:14px; color:#0f172a;">
                        Site Inspections
                    </h5>
                </div>

                <div class="card-body px-3 pt-2 pb-3" style="background:#f1f5f9;">

                    <app-table
                        :columns="columns"
                        :rows="data"
                        :pagination="pagination"
                        :loading="loading"
                        :skeleton-rows="10"
                        empty-text="No site inspections found."
                        empty-icon="ti ti-clipboard-off"
                        @page-change="getData"
                    >

                        <!-- Inspection Number -->
                        <template #inspection_number="{ row }">
                            <span style="font-weight:600; font-size:12.5px; color:#0f172a;">
                                {{ row.inspection_number }}
                            </span>
                        </template>

                        <!-- Party -->
                        <template #party="{ row }">
                            <div v-if="row.party">
                                <div style="font-weight:600; font-size:12.5px; color:#0f172a;">{{ row.party.name }}</div>
                                <div style="font-size:11px; color:#64748b;">{{ row.party.mobile }}</div>
                            </div>
                            <span v-else style="color:#cbd5e1;">-</span>
                        </template>

                        <!-- Site Inspector -->
                        <template #site_inspector="{ row }">
                            <div v-if="row.site_inspector?.user" class="d-flex align-items-center gap-2">
                                <img :src="$profileImage(row.site_inspector.user.image)" width="22" height="22"
                                    style="border-radius:50%; object-fit:cover;" />
                                <span style="font-size:12px;">{{ row.site_inspector.user.name }}</span>
                            </div>
                            <span v-else style="font-size:11px; color:#cbd5e1; font-style:italic;">Not Assigned</span>
                        </template>

                        <!-- Status -->
                        <template #status="{ row }">
                            <span class="badge" :class="statusBadge(row.status).class">
                                {{ statusBadge(row.status).label }}
                            </span>
                        </template>

                        <!-- Created By -->
                        <template #created_by="{ row }">
                            <span style="font-size:12px; color:#475569;" v-if="row.created_by">
                                {{ row.created_by.name }}
                            </span>
                            <span v-else style="color:#cbd5e1;">-</span>
                        </template>

                        <!-- Created At -->
                        <template #created_at="{ row }">
                            <span style="font-size:11.5px; color:#94a3b8;">{{ $formatDate(row.created_at) }}</span>
                        </template>

                        <!-- Action -->
                        <template #action="{ row }">
                            <div class="dropdown">
                                <button class="btn btn-sm" data-bs-toggle="dropdown">
                                    <i class="ti ti-dots"></i>
                                </button>

                                <ul class="dropdown-menu dropdown-menu-end">

                                    <!-- View Details — everyone -->
                                    <li>
                                        <a class="dropdown-item" href="javascript:void(0);"
                                            data-bs-toggle="modal" data-bs-target="#site_inspection_detail_modal"
                                            @click="$refs.siteInspectionDetailModal.open(row.id)">
                                            <i class="ti ti-eye me-1"></i> View Details
                                        </a>
                                    </li>

                                    <!-- Assign Inspector — only Admin & not yet assigned -->
                                    <li v-if="auth?.type === 'ADMIN' && !row.site_inspector">
                                        <a class="dropdown-item" href="javascript:void(0);"
                                            data-bs-toggle="modal" data-bs-target="#assign_inspector_modal"
                                            @click="$refs.assignInspectorModal.open(row)">
                                            <i class="ti ti-user-plus me-1"></i> Assign Inspector
                                        </a>
                                    </li>

                                    <!-- Upload Documents — only assigned inspector -->
                                    <li v-if="row.site_inspector?.user_id === auth?.id">
                                        <a class="dropdown-item" href="javascript:void(0);"
                                            data-bs-toggle="modal" data-bs-target="#upload_inspection_doc_modal"
                                            @click="$refs.uploadInspectionDocModal.open(row)">
                                            <i class="ti ti-upload me-1"></i> Upload Documents
                                        </a>
                                    </li>

                                    <!-- Delete — only Admin -->
                                    <li v-if="auth?.type === 'ADMIN'">
                                        <a class="dropdown-item text-danger" href="javascript:void(0);"
                                            @click="confirmDelete(row.id)">
                                            <i class="ti ti-trash me-1"></i> Delete
                                        </a>
                                    </li>

                                </ul>
                            </div>
                        </template>

                    </app-table>

                </div>

            </div>

        </div>

        <app-footer />

    </div>

    <assign-inspector-modal ref="assignInspectorModal" @updated="onAssigned" />
    <upload-inspection-doc-modal ref="uploadInspectionDocModal" @updated="onDocUploaded" />
    <site-inspection-detail-modal ref="siteInspectionDetailModal" />

</template>

<style scoped>
.bg-warning-light { background: #fef3c7; }
.text-warning { color: #f59e0b !important; }
.bg-info-light { background: #dbeafe; }
.text-info { color: #3b82f6 !important; }
.bg-success-light { background: #dcfce7; }
.text-success { color: #22c55e !important; }
</style>